/**
 * Lot AR « Projets moins figés » : ordre du planning rangé à la main (le même pour tout le monde), logique pure
 * partagée par le serveur (`PUT /api/projects/order`, `listProjects`) et le planning (poignée ⋮⋮, Alt + flèches).
 */

/** Longueur maximale du nom d'un projet (renommage dans le planning, comme le nom des outils de l'assistant). */
export const PROJECT_NAME_MAX = 120;

/** Nombre maximal d'anciens noms gardés pour un projet (les plus anciens partent en premier). */
export const FORMER_NAMES_MAX = 10;

const sameName = (a: string, b: string) =>
  a.trim().toLocaleLowerCase('fr') === b.trim().toLocaleLowerCase('fr');

/**
 * Anciens noms après un renommage (décision du PM du 15/10/2026) : l'ancien nom est ajouté (le plus récent en
 * dernier), sans doublon (casse ignorée), sauf s'il est égal au nom du client (déjà reconnu) ou au nouveau nom ;
 * un ancien nom redevenu le nom actuel est retiré ; `FORMER_NAMES_MAX` au plus.
 */
export function pushFormerName(
  formerNames: readonly string[],
  oldName: string,
  current: { newName: string; clientName: string },
): string[] {
  const kept = formerNames.filter((n) => !sameName(n, current.newName) && !sameName(n, oldName));
  const skip = !oldName.trim() || sameName(oldName, current.newName) || sameName(oldName, current.clientName);
  const next = skip ? kept : [...kept, oldName.trim()];
  return next.slice(-FORMER_NAMES_MAX);
}

/** Ce qu'il faut d'un projet pour le ranger : sa position (null : pas rangé), sa première date, sa création. */
export interface OrderableProject {
  position: number | null;
  /** Première date du projet (`AAAA-MM-JJ`), `9999-12-31` s'il n'en a aucune. */
  start: string;
  createdAt: number;
}

/**
 * Ordre du planning : les projets rangés à la main d'abord (par position), puis ceux qui ne le sont pas encore
 * (aucun rangement, ou projet arrivé après le dernier rangement) par première date, puis par création. Sans
 * aucune position, c'est l'ordre par date d'avant le lot AR.
 */
export function compareProjectOrder(a: OrderableProject, b: OrderableProject): number {
  if (a.position !== null && b.position !== null && a.position !== b.position) return a.position - b.position;
  if (a.position !== null && b.position === null) return -1;
  if (a.position === null && b.position !== null) return 1;
  return a.start.localeCompare(b.start) || a.createdAt - b.createdAt;
}

/**
 * Nouvel ordre complet après le rangement d'une liste filtrée (Evolusite / Partenaires, projets terminés
 * masqués) : les projets visibles reprennent, dans leur nouvel ordre, les places qu'ils occupaient dans l'ordre
 * complet ; les projets masqués gardent exactement leur place. Les identifiants inconnus de `full` sont ignorés
 * (le serveur les refuse avant), un doublon de `visible` ne compte qu'une fois.
 *
 * Ex. : complet A b C d E (b, d masqués), visibles rangés E A C → E b A d C.
 */
export function mergeProjectOrder(full: readonly string[], visible: readonly string[]): string[] {
  const known = new Set(full);
  const queue = [...new Set(visible)].filter((id) => known.has(id));
  const moved = new Set(queue);
  let next = 0;
  return full.map((id) => (moved.has(id) ? queue[next++]! : id));
}

/** Identifiants de `ids` absents de `full` (projets inconnus ou supprimés entre-temps). */
export function unknownProjectIds(full: readonly string[], ids: readonly string[]): string[] {
  const known = new Set(full);
  return ids.filter((id) => !known.has(id));
}

/** Alt + ↑ / ↓ sur la poignée : le projet monte (`-1`) ou descend (`+1`) d'un rang ; null s'il est déjà au bout. */
export function moveProjectBy(ids: readonly string[], id: string, delta: -1 | 1): string[] | null {
  const from = ids.indexOf(id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= ids.length) return null;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}

/**
 * Glisser-déposer de la poignée : le projet `id` est déposé à l'emplacement `slot` (0 = tout en haut,
 * `ids.length` = tout en bas, compté dans la liste AVANT le déplacement) ; null si rien ne change.
 */
export function moveProjectTo(ids: readonly string[], id: string, slot: number): string[] | null {
  const from = ids.indexOf(id);
  if (from < 0) return null;
  const target = Math.max(0, Math.min(ids.length, slot));
  const to = target > from ? target - 1 : target;
  if (to === from) return null;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
}
